import React, { useState, useEffect, useCallback, useRef } from 'react';

const S = {
  page: {
    height: '100vh', display: 'flex', flexDirection: 'column',
    background: '#0f172a', color: '#e2e8f0', fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
    fontSize: 14, overflow: 'hidden',
  },
  topBar: {
    padding: '14px 20px', borderBottom: '1px solid #1e293b',
    display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0,
  },
  title: { fontSize: 20, fontWeight: 700, color: '#f1f5f9', margin: 0 },
  badge: {
    background: '#1e3a5f', color: '#60a5fa', fontSize: 11, fontWeight: 600,
    padding: '2px 8px', borderRadius: 20, border: '1px solid #2563eb44',
  },
  refreshBtn: {
    marginLeft: 'auto', padding: '6px 14px', borderRadius: 8,
    background: '#1e293b', border: '1px solid #334155', color: '#94a3b8',
    cursor: 'pointer', fontSize: 13, fontWeight: 500,
  },
  cards: {
    display: 'flex', gap: 12, padding: '12px 20px', flexShrink: 0,
    overflowX: 'auto',
  },
  card: {
    background: '#1e293b', border: '1px solid #334155', borderRadius: 10,
    padding: '10px 16px', minWidth: 130, flexShrink: 0,
  },
  cardLabel: { color: '#64748b', fontSize: 11, fontWeight: 500, marginBottom: 4 },
  toolbar: {
    padding: '8px 20px', borderBottom: '1px solid #1e293b',
    display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap',
  },
  viewBtn: (active) => ({
    padding: '5px 12px', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer',
    background: active ? '#2563eb' : '#1e293b',
    color: active ? '#fff' : '#94a3b8',
    border: active ? '1px solid #3b82f6' : '1px solid #334155',
  }),
  smallBtn: {
    padding: '5px 10px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer',
    background: '#1e293b', color: '#94a3b8', border: '1px solid #334155',
  },
  search: {
    marginLeft: 'auto', padding: '5px 10px', borderRadius: 8, fontSize: 13,
    background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0',
    outline: 'none', width: 200,
  },
  pageInfo: { color: '#64748b', fontSize: 12, whiteSpace: 'nowrap' },
  navBtn: (disabled) => ({
    padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: disabled ? 'not-allowed' : 'pointer',
    background: '#1e293b', border: '1px solid #334155',
    color: disabled ? '#334155' : '#94a3b8',
  }),
  tableWrap: { flex: 1, overflowY: 'auto', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 900 },
  th: {
    padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600,
    color: '#64748b', borderBottom: '1px solid #1e293b',
    background: '#0f172a', position: 'sticky', top: 0, zIndex: 2, textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  thR: {
    padding: '8px 12px', textAlign: 'right', fontSize: 11, fontWeight: 600,
    color: '#64748b', borderBottom: '1px solid #1e293b',
    background: '#0f172a', position: 'sticky', top: 0, zIndex: 2, textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  tr: (isGroup) => ({
    background: isGroup ? '#111827' : 'transparent',
    borderBottom: '1px solid #1e293b',
    cursor: isGroup ? 'pointer' : 'default',
    transition: 'background 0.1s',
  }),
  td: { padding: '8px 12px', fontSize: 13, color: '#cbd5e1' },
  tdR: { padding: '8px 12px', fontSize: 13, color: '#cbd5e1', textAlign: 'right' },
  arrow: (open) => ({
    display: 'inline-block', marginRight: 6, fontSize: 10, color: '#64748b',
    transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s',
  }),
  runPill: (type) => {
    const colors = {
      main: { bg: '#1a3354', color: '#60a5fa', border: '#2563eb44' },
      visual_fix: { bg: '#2d1f4e', color: '#a78bfa', border: '#7c3aed44' },
      images: { bg: '#1a3328', color: '#34d399', border: '#059669' },
      visual_qa: { bg: '#2d2a1f', color: '#fbbf24', border: '#d9770644' },
    };
    const c = colors[type] || colors.main;
    return {
      display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11,
      background: c.bg, color: c.color, border: `1px solid ${c.border}`, fontWeight: 500,
    };
  },
  modelPill: {
    display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11,
    background: '#1e3a5f', color: '#93c5fd', border: '1px solid #1d4ed844', fontWeight: 500,
  },
  totalRow: {
    background: '#111827', fontWeight: 600, borderTop: '2px solid #334155',
  },
};

const fmt = (n) => n == null ? '—' : n.toLocaleString();
const fmtCost = (n) => n == null || n === 0 ? '—' : `$${n.toFixed(4)}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + (iso.endsWith('Z') ? '' : 'Z'));
  return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }).replace(',', '');
};
const groupKey = (view, run) => {
  if (view === 'date') return run.timestamp ? run.timestamp.slice(0, 10) : 'Unknown';
  if (view === 'month') return run.timestamp ? run.timestamp.slice(0, 7) : 'Unknown';
  if (view === 'year') return run.timestamp ? run.timestamp.slice(0, 4) : 'Unknown';
  if (view === 'model') return run.model || 'Unknown';
  return run.template_id || 'unknown';
};

export default function CostMonitor({ apiBase, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('lesson');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const base = (apiBase || '').replace(/\/+$/, '');
      const res = await fetch(`${base}/board/cost-monitor`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase, token]);

  useEffect(() => { load(); }, [load]);

  const toggleAll = (allKeys, open) => {
    const next = {};
    allKeys.forEach(k => { next[k] = open; });
    setExpanded(next);
  };

  if (loading) return (
    <div style={{ ...S.page, alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#64748b', fontSize: 16 }}>Loading cost data…</div>
    </div>
  );
  if (error) return (
    <div style={{ ...S.page, alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#f87171', fontSize: 14 }}>Error: {error}</div>
    </div>
  );

  const totals = data?.totals || {};
  const lessons = data?.lessons || [];
  const allRuns = lessons.flatMap(l => l.runs || []);

  // Build groups for current view
  let groups = [];
  if (view === 'lesson') {
    groups = lessons.map(l => ({
      key: l.template_id,
      label: l.lesson_name || l.template_id,
      sub: l.template_id,
      runs: l.runs || [],
      totals: l.totals,
      created_at: l.created_at,
    }));
  } else {
    const map = {};
    allRuns.forEach(r => {
      const k = groupKey(view, r);
      if (!map[k]) map[k] = { key: k, label: k, runs: [], totals: { input_tokens: 0, output_tokens: 0, llm_cost: 0, img_cost: 0, images: 0 } };
      map[k].runs.push(r);
      const t = map[k].totals;
      t.input_tokens += r.input_tokens || 0;
      t.output_tokens += r.output_tokens || 0;
      t.llm_cost = parseFloat((t.llm_cost + (r.llm_cost || 0)).toFixed(4));
      t.img_cost = parseFloat((t.img_cost + (r.img_cost || 0)).toFixed(4));
      t.images += r.images || 0;
    });
    groups = Object.values(map).sort((a, b) => b.key.localeCompare(a.key));
  }

  // Filter
  const q = search.toLowerCase();
  const filtered = q
    ? groups.filter(g =>
        g.label.toLowerCase().includes(q) ||
        (g.sub || '').toLowerCase().includes(q) ||
        g.runs.some(r => (r.model || '').toLowerCase().includes(q))
      )
    : groups;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safeP = Math.min(page, totalPages);
  const pageGroups = filtered.slice((safeP - 1) * PAGE_SIZE, safeP * PAGE_SIZE);
  const allKeys = pageGroups.map(g => g.key);
  const totalCost = totals.llm_cost + totals.img_cost;

  return (
    <div style={S.page}>
      {/* Top bar */}
      <div style={S.topBar}>
        <span style={S.title}>Cost Monitor</span>
        <span style={S.badge}>{lessons.length} lesson{lessons.length !== 1 ? 's' : ''}</span>
        <button style={S.refreshBtn} onClick={load}>Refresh</button>
      </div>

      {/* Summary cards */}
      <div style={S.cards}>
        {[
          { label: 'Total Cost', val: `$${totalCost.toFixed(4)}`, color: '#f87171' },
          { label: 'LLM Cost', val: `$${(totals.llm_cost || 0).toFixed(4)}`, color: '#fb923c' },
          { label: 'Image Cost', val: `$${(totals.img_cost || 0).toFixed(4)}`, color: '#34d399' },
          { label: 'Input Tokens', val: (totals.input_tokens || 0).toLocaleString(), color: '#60a5fa' },
          { label: 'Output Tokens', val: (totals.output_tokens || 0).toLocaleString(), color: '#60a5fa' },
          { label: 'Images', val: (totals.images || 0).toLocaleString(), color: '#a78bfa' },
        ].map(c => (
          <div key={c.label} style={S.card}>
            <div style={S.cardLabel}>{c.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: c.color }}>{c.val}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={S.toolbar}>
        {['lesson', 'date', 'month', 'year', 'model'].map(v => (
          <button key={v} style={S.viewBtn(view === v)} onClick={() => { setView(v); setPage(1); }}>
            {v.charAt(0).toUpperCase() + v.slice(1)}
          </button>
        ))}
        {view === 'lesson' && <>
          <button style={S.smallBtn} onClick={() => toggleAll(allKeys, true)}>Expand All</button>
          <button style={S.smallBtn} onClick={() => toggleAll(allKeys, false)}>Collapse All</button>
        </>}
        <input
          style={S.search} placeholder="Search lesson or model…"
          value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <span style={S.pageInfo}>Page {safeP}/{totalPages}</span>
        <button style={S.navBtn(safeP <= 1)} disabled={safeP <= 1} onClick={() => setPage(p => p - 1)}>{'<'}</button>
        <span style={{ ...S.navBtn(false), background: '#2563eb', color: '#fff', border: '1px solid #3b82f6' }}>{safeP}</span>
        <button style={S.navBtn(safeP >= totalPages)} disabled={safeP >= totalPages} onClick={() => setPage(p => p + 1)}>{'>'}</button>
      </div>

      {/* Table */}
      <div style={S.tableWrap}>
        {filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#475569' }}>
            No cost data yet. Cost data is recorded as new lessons are generated.
          </div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>#</th>
                <th style={S.th}>{view === 'lesson' ? 'Lesson' : view.charAt(0).toUpperCase() + view.slice(1)}</th>
                {view === 'lesson' && <th style={S.th}>Run</th>}
                <th style={S.th}>Model</th>
                <th style={S.thR}>Input Tokens</th>
                <th style={S.thR}>Output Tokens</th>
                <th style={S.thR}>Images</th>
                <th style={S.thR}>LLM Cost</th>
                <th style={S.thR}>Img Cost</th>
                <th style={S.thR}>Total</th>
              </tr>
            </thead>
            <tbody>
              {pageGroups.map((g, gi) => {
                const isOpen = !!expanded[g.key];
                const rowTotal = (g.totals.llm_cost || 0) + (g.totals.img_cost || 0);
                const runCount = g.runs.length;
                return (
                  <React.Fragment key={g.key}>
                    {/* Group row */}
                    <tr
                      style={S.tr(true)}
                      onClick={() => view === 'lesson' && setExpanded(prev => ({ ...prev, [g.key]: !prev[g.key] }))}
                      onMouseEnter={e => e.currentTarget.style.background = '#1a2744'}
                      onMouseLeave={e => e.currentTarget.style.background = '#111827'}
                    >
                      <td style={S.td}>{(safeP - 1) * PAGE_SIZE + gi + 1}</td>
                      <td style={S.td}>
                        {view === 'lesson' && <span style={S.arrow(isOpen)}>›</span>}
                        <span style={{ fontWeight: 600, color: '#f1f5f9' }}>{g.label}</span>
                        {view === 'lesson' && g.sub && (
                          <span style={{ color: '#475569', fontSize: 11, marginLeft: 6 }}>
                            {g.sub} · {fmtDate(g.created_at)} · {runCount} run{runCount !== 1 ? 's' : ''}
                          </span>
                        )}
                      </td>
                      {view === 'lesson' && <td style={S.td}></td>}
                      <td style={S.td}></td>
                      <td style={S.tdR}>{fmt(g.totals.input_tokens)}</td>
                      <td style={S.tdR}>{fmt(g.totals.output_tokens)}</td>
                      <td style={S.tdR}>{g.totals.images || '—'}</td>
                      <td style={{ ...S.tdR, color: '#fb923c' }}>{fmtCost(g.totals.llm_cost)}</td>
                      <td style={{ ...S.tdR, color: '#34d399' }}>{fmtCost(g.totals.img_cost)}</td>
                      <td style={{ ...S.tdR, color: '#f87171', fontWeight: 600 }}>{fmtCost(rowTotal)}</td>
                    </tr>

                    {/* Sub rows (only in lesson view when expanded) */}
                    {view === 'lesson' && isOpen && g.runs.map((r, ri) => {
                      const runTotal = (r.llm_cost || 0) + (r.img_cost || 0);
                      return (
                        <tr key={ri} style={{ ...S.tr(false), background: '#0d1526' }}>
                          <td style={S.td}></td>
                          <td style={{ ...S.td, paddingLeft: 28, color: '#64748b', fontSize: 12 }}>
                            {fmtDate(r.timestamp)}
                          </td>
                          <td style={S.td}><span style={S.runPill(r.run_type)}>{r.run_type || 'main'}</span></td>
                          <td style={S.td}><span style={S.modelPill}>{r.model || '—'}</span></td>
                          <td style={S.tdR}>{fmt(r.input_tokens)}</td>
                          <td style={S.tdR}>{fmt(r.output_tokens)}</td>
                          <td style={S.tdR}>{r.images || '—'}</td>
                          <td style={{ ...S.tdR, color: '#fb923c' }}>{fmtCost(r.llm_cost)}</td>
                          <td style={{ ...S.tdR, color: '#34d399' }}>{fmtCost(r.img_cost)}</td>
                          <td style={{ ...S.tdR, color: '#f87171' }}>{fmtCost(runTotal)}</td>
                        </tr>
                      );
                    })}

                    {/* Non-lesson views: show each run as a sub-row directly */}
                    {view !== 'lesson' && isOpen === undefined && false}
                  </React.Fragment>
                );
              })}

              {/* Non-lesson views: show all runs expanded by default */}
              {view !== 'lesson' && pageGroups.map((g) =>
                g.runs.map((r, ri) => {
                  const runTotal = (r.llm_cost || 0) + (r.img_cost || 0);
                  return (
                    <tr key={`${g.key}-${ri}`} style={{ ...S.tr(false), background: '#0d1526', display: 'none' }}>
                      <td style={S.td}></td>
                      <td style={{ ...S.td, paddingLeft: 28, color: '#64748b', fontSize: 12 }}>
                        {r.lesson_name || r.template_id} · {fmtDate(r.timestamp)}
                      </td>
                      <td style={S.td}><span style={S.runPill(r.run_type)}>{r.run_type || 'main'}</span></td>
                      <td style={S.td}><span style={S.modelPill}>{r.model || '—'}</span></td>
                      <td style={S.tdR}>{fmt(r.input_tokens)}</td>
                      <td style={S.tdR}>{fmt(r.output_tokens)}</td>
                      <td style={S.tdR}>{r.images || '—'}</td>
                      <td style={{ ...S.tdR, color: '#fb923c' }}>{fmtCost(r.llm_cost)}</td>
                      <td style={{ ...S.tdR, color: '#34d399' }}>{fmtCost(r.img_cost)}</td>
                      <td style={{ ...S.tdR, color: '#f87171' }}>{fmtCost(runTotal)}</td>
                    </tr>
                  );
                })
              )}

              {/* Total row */}
              <tr style={S.totalRow}>
                <td style={S.td}></td>
                <td style={{ ...S.td, color: '#94a3b8', fontWeight: 700 }}>
                  TOTAL — {filtered.length} {view === 'lesson' ? 'lesson' : view}{filtered.length !== 1 ? 's' : ''}
                </td>
                {view === 'lesson' && <td style={S.td}></td>}
                <td style={S.td}></td>
                <td style={{ ...S.tdR, color: '#60a5fa' }}>{fmt(totals.input_tokens)}</td>
                <td style={{ ...S.tdR, color: '#60a5fa' }}>{fmt(totals.output_tokens)}</td>
                <td style={S.tdR}>{totals.images || '—'}</td>
                <td style={{ ...S.tdR, color: '#fb923c', fontWeight: 700 }}>{fmtCost(totals.llm_cost)}</td>
                <td style={{ ...S.tdR, color: '#34d399', fontWeight: 700 }}>{fmtCost(totals.img_cost)}</td>
                <td style={{ ...S.tdR, color: '#f87171', fontWeight: 700 }}>{fmtCost(totalCost)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
